import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Handshake as HandshakeIcon, Plus, Trash2, Save, X, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import ImagePasteField from './ImagePasteField';
import { PARTNER_ICONS } from '../lib/partnerIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';

const Lbl = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] tracking-widest uppercase text-neutral-500">{label}</span>
    {children}
  </label>
);

const Msg = ({ msg, testid }) => (msg.text ? <span data-testid={testid} className={`text-[12.5px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span> : null);

const PageSettingsEditor = ({ page, setPage, headers }) => {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', err: false });
  const upd = (k, v) => setPage(prev => ({ ...prev, [k]: v }));
  const updHl = (i, k, v) => setPage(prev => ({ ...prev, highlights: prev.highlights.map((h, j) => (j === i ? { ...h, [k]: v } : h)) }));
  const save = async () => {
    setSaving(true); setMsg({ text: '', err: false });
    try { setPage((await axios.put(`${API}/admin/settings/partners-page`, page, { headers })).data); setMsg({ text: 'Page saved.', err: false }); }
    catch (e) { setMsg({ text: e?.response?.data?.detail || 'Save failed', err: true }); }
    finally { setSaving(false); }
  };
  return (
    <section data-testid="partners-page-editor" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-[16px] font-semibold text-neutral-900 dark:text-neutral-100">Page content</h3>
        <div className="flex flex-wrap gap-5">
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input data-testid="partners-strip-toggle" type="checkbox" checked={page.show_strip !== false} onChange={(e) => upd('show_strip', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
          Show logo strip on home page
        </label>
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input data-testid="partners-enabled-toggle" type="checkbox" checked={!!page.enabled} onChange={(e) => upd('enabled', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
          Show Partners page on website
        </label>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Lbl label="Small label above heading"><input data-testid="partners-eyebrow-input" className={inputCls} value={page.eyebrow} maxLength={40} onChange={(e) => upd('eyebrow', e.target.value)} /></Lbl>
        <Lbl label="Script tagline (right side)"><input data-testid="partners-script-input" className={inputCls} value={page.script_text} maxLength={80} onChange={(e) => upd('script_text', e.target.value)} /></Lbl>
        <Lbl label="Heading *"><input data-testid="partners-heading-input" className={inputCls} value={page.heading} maxLength={80} onChange={(e) => upd('heading', e.target.value)} /></Lbl>
        <Lbl label="Heading accent (2nd line, light blue)"><input data-testid="partners-accent-input" className={inputCls} value={page.heading_accent} maxLength={80} onChange={(e) => upd('heading_accent', e.target.value)} /></Lbl>
        <Lbl label="Intro text" className="sm:col-span-2"><textarea data-testid="partners-intro-input" rows={3} className={inputCls + ' resize-none'} value={page.intro} maxLength={400} onChange={(e) => upd('intro', e.target.value)} /></Lbl>
        <div className="sm:col-span-2">
          <span className="text-[11px] tracking-widest uppercase text-neutral-500">Hero background image</span>
          <ImagePasteField value={page.hero_image} onChange={(v) => upd('hero_image', v)} />
        </div>
      </div>
      <div>
        <span className="text-[11px] tracking-widest uppercase text-neutral-500">Highlights strip</span>
        <div className="mt-2 space-y-2">
          {page.highlights.map((h, i) => (
            <div key={i} data-testid={`partners-hl-row-${i}`} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
              <select data-testid={`partners-hl-icon-${i}`} className={inputCls + ' sm:w-36'} value={h.icon} onChange={(e) => updHl(i, 'icon', e.target.value)}>
                {Object.entries(PARTNER_ICONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <input data-testid={`partners-hl-title-${i}`} className={inputCls + ' sm:flex-[1]'} placeholder="Title" maxLength={40} value={h.title} onChange={(e) => updHl(i, 'title', e.target.value)} />
              <input data-testid={`partners-hl-text-${i}`} className={inputCls + ' sm:flex-[2]'} placeholder="Short text" maxLength={120} value={h.text} onChange={(e) => updHl(i, 'text', e.target.value)} />
              <button data-testid={`partners-hl-delete-${i}`} onClick={() => setPage(prev => ({ ...prev, highlights: prev.highlights.filter((_, j) => j !== i) }))} className="p-2 text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button data-testid="partners-hl-add" disabled={page.highlights.length >= 6} onClick={() => setPage(prev => ({ ...prev, highlights: [...prev.highlights, { icon: 'shield', title: '', text: '' }] }))} className="mt-2 inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[12.5px] disabled:opacity-50"><Plus size={13} /> Add highlight</button>
      </div>
      <div className="flex items-center justify-end gap-3">
        <Msg msg={msg} testid="partners-page-msg" />
        <button data-testid="partners-page-save-btn" onClick={save} disabled={saving || !page.heading.trim()} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save page'}</button>
      </div>
    </section>
  );
};

const PartnerForm = ({ editing, setEditing, onSave, saving, error }) => {
  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setEditing(null)}>
      <div data-testid="partner-form-modal" className="my-10 w-full max-w-lg bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[18px]">{editing.isNew ? 'Add partner' : 'Edit partner'}</h3>
          <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Lbl label="Name *"><input data-testid="partner-name-input" className={inputCls} value={editing.name} maxLength={60} onChange={(e) => upd('name', e.target.value)} /></Lbl>
          <Lbl label="Type tag (e.g. Exports)"><input data-testid="partner-tag-input" className={inputCls} value={editing.tag} maxLength={40} onChange={(e) => upd('tag', e.target.value)} /></Lbl>
          <div className="grid sm:grid-cols-2 gap-3">
            <Lbl label="Website (optional)"><input data-testid="partner-website-input" className={inputCls} placeholder="https://example.com" value={editing.website || ''} onChange={(e) => upd('website', e.target.value)} /></Lbl>
            <Lbl label="Instagram (optional)"><input data-testid="partner-instagram-input" className={inputCls} placeholder="@brand or profile link" value={editing.instagram || ''} onChange={(e) => upd('instagram', e.target.value)} /></Lbl>
          </div>
          <Lbl label="Description"><textarea data-testid="partner-description-input" rows={3} className={inputCls + ' resize-none'} value={editing.description} maxLength={300} onChange={(e) => upd('description', e.target.value)} /></Lbl>
          <div>
            <span className="text-[11px] tracking-widest uppercase text-neutral-500">Logo (optional — a name badge is shown if empty)</span>
            <ImagePasteField value={editing.logo} onChange={(v) => upd('logo', v)} />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px]">
            <input data-testid="partner-active-checkbox" type="checkbox" checked={!!editing.is_active} onChange={(e) => upd('is_active', e.target.checked)} className="w-4 h-4 accent-emerald-600" /> Visible on website
          </label>
          {error && <div data-testid="partner-form-error" className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-[12.5px] text-red-700 dark:text-red-300">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
          <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
          <button data-testid="partner-save-btn" onClick={onSave} disabled={saving || !editing.name.trim()} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const PartnersPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [page, setPage] = useState(null);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partners`, { headers });
      setRows(data.partners); setPage(prev => prev || data.page);
    } catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const patch = (id, body) => axios.patch(`${API}/admin/partners/${id}`, body, { headers });
  const save = async () => {
    setSaving(true); setMsg('');
    const body = { name: editing.name, tag: editing.tag || '', description: editing.description || '', logo: editing.logo || '', website: editing.website || '', instagram: editing.instagram || '', is_active: !!editing.is_active };
    try {
      if (editing.isNew) await axios.post(`${API}/admin/partners`, body, { headers });
      else await patch(editing.id, body);
      setEditing(null); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const move = async (i, d) => {
    const next = [...rows];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setRows(next);
    try { await Promise.all(next.map((p, j) => (p.sort_order !== j ? patch(p.id, { sort_order: j }) : null))); await load(); }
    catch { setMsg('Reorder failed'); load(); }
  };
  const toggle = async (p) => { try { await patch(p.id, { is_active: !p.is_active }); await load(); } catch { setMsg('Update failed'); } };
  const remove = async (p) => {
    if (!window.confirm(`Delete partner "${p.name}"?`)) return;
    try { await axios.delete(`${API}/admin/partners/${p.id}`, { headers }); await load(); } catch { setMsg('Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="partners-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><HandshakeIcon size={20} className="text-neutral-400" /> Partners</h2>
          <p className="text-[12.5px] text-neutral-500">Manage the Our Partners page at /partners.</p>
        </div>
        <a data-testid="partners-view-link" href="/partners" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><ExternalLink size={13} /> View page</a>
      </div>
      {msg && !editing && <div className="text-red-600 text-[13px]" data-testid="partners-error">{msg}</div>}

      <section className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="text-[16px] font-semibold">Partner cards</h3>
          <button data-testid="add-partner-btn" onClick={() => { setMsg(''); setEditing({ isNew: true, name: '', tag: '', description: '', logo: '', website: '', instagram: '', is_active: true }); }} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-[13px] font-semibold"><Plus size={14} /> Add partner</button>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rows.length === 0 && <div className="p-6 text-center text-neutral-500 text-[13.5px]">No partners yet.</div>}
          {rows.map((p, i) => (
            <div key={p.id} data-testid={`partner-row-${p.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex flex-col">
                <button data-testid={`partner-up-${p.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowUp size={13} /></button>
                <button data-testid={`partner-down-${p.id}`} disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <span className="w-11 h-11 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 grid place-items-center overflow-hidden">
                {p.logo ? <img src={p.logo} alt="" className="max-w-full max-h-full object-contain" /> : <span className="font-serif text-[18px] text-emerald-700">{p.name.charAt(0)}</span>}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px] truncate">{p.name}</div>
                <div className="text-[11.5px] text-neutral-500 truncate">{p.tag || '—'}</div>
              </div>
              <button data-testid={`partner-toggle-${p.id}`} onClick={() => toggle(p)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                {p.is_active ? <Eye size={11} /> : <EyeOff size={11} />} {p.is_active ? 'Visible' : 'Hidden'}
              </button>
              <button data-testid={`partner-edit-${p.id}`} onClick={() => { setMsg(''); setEditing({ ...p, isNew: false }); }} className="px-2.5 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700">Edit</button>
              <button data-testid={`partner-delete-${p.id}`} onClick={() => remove(p)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {page && <PageSettingsEditor page={page} setPage={setPage} headers={headers} />}
      {editing && <PartnerForm editing={editing} setEditing={(v) => { setEditing(v); if (!v) setMsg(''); }} onSave={save} saving={saving} error={msg} />}
    </div>
  );
};

export default PartnersPanel;
